import { useToast } from '@chakra-ui/react';
import { FormikTouched, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import {
  ApplicationAddressDTO,
  ApplicationTypeDTO,
} from '../../services/applications/dto';
import { useApplicationsService } from '../../services/applications/applicationsService';
import { COUNTRIES } from '../../utils/countries';
import {
  ApplicationController,
  ApplicationFieldError,
  ApplicationFormValues,
} from './interfaces';

/**
 * Etiquetas de los campos, para los chips del resumen de errores. La clave es el path
 * de formik, que es también el `id` del input: así el chip puede hacerle foco.
 */
const FIELD_LABELS: Record<string, string> = {
  name: 'Nombre del comercio',
  types: 'Categoría',
  address: 'Dirección',
  'point.lat': 'Dirección',
  state: 'Provincia',
  city: 'Ciudad',
  contactFirstName: 'Nombre de contacto',
  contactLastName: 'Apellido de contacto',
  email: 'Email',
  password: 'Contraseña',
  country: 'País',
  phone: 'Teléfono',
  benefitType: 'Tipo de beneficio',
  benefitTag: 'Valor',
  benefitTitle: 'Título del beneficio',
  benefitDescription: 'Descripción',
  benefitFile: 'Foto del beneficio',
};

/**
 * `benefitTag` (el "valor") no aplica al canje: el detalle del backoffice
 * (`BenefitTypeDetail`) no muestra ningún campo para `exchange`, y en producción los
 * beneficios de ese tipo no lo tienen. Los campos del paso 2 dependen del tipo elegido.
 */
const stepFields = (step: number, values: ApplicationFormValues): string[] =>
  step === 0
    ? STEP_FIELDS[0]
    : STEP_FIELDS[1].filter(
        (field) => field !== 'benefitTag' || values.benefitType !== 'exchange'
      );

const STEP_FIELDS: string[][] = [
  [
    'name',
    'types',
    'address',
    'point.lat',
    'state',
    'city',
    'contactFirstName',
    'contactLastName',
    'email',
    'password',
    'country',
    'phone',
  ],
  ['benefitType', 'benefitTag', 'benefitTitle', 'benefitDescription', 'benefitFile'],
];

const STEP_TOUCHED: FormikTouched<ApplicationFormValues>[] = [
  {
    name: true,
    types: true,
    address: true,
    // `point` se marca campo por campo: los errores de Yup para un objeto son anidados
    // y con un booleano el `getIn` no los encuentra.
    point: { lat: true, lng: true },
    state: true,
    city: true,
    postalCode: true,
    contactFirstName: true,
    contactLastName: true,
    email: true,
    password: true,
    country: { label: true, value: true, prefix: true },
    phone: true,
  },
  {
    benefitType: true,
    benefitTag: true,
    benefitTitle: true,
    benefitDescription: true,
    // `benefitFile` es un File y el tipo de `touched` para un objeto no acepta un
    // booleano. Se marca aparte al elegir la foto, en el propio campo.
  },
];

/**
 * La búsqueda es explícita: la dispara el botón o la tecla Enter, no el tipeo.
 *
 * Antes iba con debounce, pero eso le pegaba al geocoder con direcciones a medio escribir
 * —una consulta por cada pausa— y devolvía resultados que no servían. Con un disparador
 * explícito se consulta una vez, con la dirección completa.
 */
const ADDRESS_MIN_CHARS = 4;

export const ApplicationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Escribe el nombre completo del comercio')
    .max(60, 'Máximo 60 caracteres')
    .required('Falta el nombre del comercio'),
  types: Yup.array()
    .of(Yup.string())
    .min(1, 'Elige al menos una categoría')
    .required('Elige al menos una categoría'),
  address: Yup.string().required('Elige una dirección de la lista'),
  // Se precargan con la dirección elegida; el `required` sólo pega cuando el buscador no
  // los devolvió y hay que escribirlos a mano.
  state: Yup.string().required('Falta la provincia'),
  city: Yup.string().required('Falta la ciudad'),
  postalCode: Yup.string(),
  point: Yup.object().shape({
    lat: Yup.number().nullable().required('Elige una dirección de la lista'),
    lng: Yup.number().nullable().required('Elige una dirección de la lista'),
  }),
  contactFirstName: Yup.string().required('Falta tu nombre'),
  contactLastName: Yup.string().required('Falta tu apellido'),
  email: Yup.string()
    .email('Falta el dominio. Ejemplo: contacto@tucomercio.com')
    .required('Falta el email'),
  // Sólo un mínimo de largo: el registro de bikleta-auth tampoco pide mayúsculas ni
  // caracteres especiales, y el alta ya tiene bastante fricción.
  password: Yup.string()
    .min(8, 'Usa al menos 8 caracteres')
    .required('Elige una contraseña'),
  country: Yup.object().nullable().required('Elige el país'),
  phone: Yup.string().required('Falta el teléfono'),

  benefitType: Yup.string().required('Elige el tipo de beneficio'),
  // El canje no lleva valor; para descuento y promoción sí es obligatorio.
  benefitTag: Yup.string().when('benefitType', {
    is: (type: string) => type !== 'exchange',
    then: Yup.string().required('Falta el valor'),
    otherwise: Yup.string(),
  }),
  benefitTitle: Yup.string().required('Falta el título del beneficio'),
  pointsCost: Yup.number()
    .typeError('Tiene que ser un número')
    .min(0, 'Tiene que ser 0 o mayor')
    .nullable(),
  benefitDescription: Yup.string()
    .max(500, 'Máximo 500 caracteres')
    .required('Cuéntale al ciclista en qué consiste'),
  benefitFile: Yup.mixed().required('Sube una foto del beneficio'),
});

export const useApplicationController = (): ApplicationController => {
  const service = useApplicationsService();
  const toast = useToast();

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [types, setTypes] = useState<ApplicationTypeDTO[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [addressResults, setAddressResults] = useState<ApplicationAddressDTO[]>(
    []
  );
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  /** Aviso del buscador: faltan caracteres o no hubo resultados. */
  const [addressNotice, setAddressNotice] = useState<string | null>(null);
  const [benefitPreviewUrl, setBenefitPreviewUrl] = useState<string | null>(null);
  /** Texto visible del buscador. Al elegir una opción pasa a ser la dirección completa. */
  const [addressQuery, setAddressQuery] = useState('');
  /**
   * Si el país salió del buscador de direcciones, el select lo muestra como detectado en
   * vez de pedirlo de nuevo. Sigue siendo editable: la detección puede errarle.
   */
  const [isCountryDetected, setIsCountryDetected] = useState(false);

  /**
   * Sin categorías el formulario no se puede completar, así que el error se muestra en el
   * propio campo con un botón para reintentar, y no como un toast que desaparece y deja
   * al comercio llenando datos que no va a poder enviar.
   *
   * El motivo se distingue porque los tres casos se resuelven distinto: un 404 es que el
   * back todavía no está deployado, un 429 se arregla esperando, y el resto es red.
   */
  const loadTypes = useCallback(async () => {
    setIsLoadingTypes(true);
    setTypesError(null);

    try {
      setTypes(await service.getTypes());
    } catch (error: any) {
      const status = error?.response?.status;

      setTypesError(
        status === 404
          ? 'El servicio de categorías no está disponible en este entorno.'
          : status === 429
          ? 'Demasiados intentos. Espera unos minutos y vuelve a probar.'
          : 'No pudimos cargar las categorías.'
      );
    } finally {
      setIsLoadingTypes(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values: ApplicationFormValues) => {
    setIsSubmitting(true);

    try {
      // La imagen va primero y por separado: así la solicitud viaja como JSON liviano y
      // una subida que falla no deja una solicitud a medio cargar.
      setUploadStatus('Subiendo la foto...');
      const imageUrl = await service.uploadImage(
        values.benefitFile as File,
        'comercios-aliados/beneficios'
      );

      setUploadStatus('Enviando...');

      const socials = {
        ...(values.web && { web: values.web.trim() }),
        ...(values.instagram && { instagram: values.instagram.trim() }),
        ...(values.whatsapp && {
          whatsapp: `${values.country?.prefix ?? ''}${values.whatsapp}`,
        }),
      };

      const code = await service.createApplication({
        commerce: {
          name: values.name,
          contactFirstName: values.contactFirstName,
          contactLastName: values.contactLastName,
          email: values.email,
          password: values.password,
          phone: `${values.country?.prefix ?? ''}${values.phone}`,
          address: values.address,
          location: {
            type: 'Point',
            coordinates: [
              values.point.lng as number,
              values.point.lat as number,
            ],
          },
          types: values.types,
          /*
           * Provincia, ciudad y CP van con lo que quedó en el formulario y no con lo que
           * devolvió el buscador: si el comercio corrigió la detección, esa corrección es
           * la buena.
           */
          ...(values.locationMetadata && {
            locationMetadata: {
              ...values.locationMetadata,
              ...(values.state && { state: values.state }),
              ...(values.city && { city: values.city }),
              ...(values.postalCode && { postalCode: values.postalCode }),
              ...(values.country && {
                country: values.country.label,
                countryCode: values.country.value,
              }),
            },
          }),
          ...(Object.keys(socials).length && { socials }),
        },
        benefit: {
          title: values.benefitTitle,
          description: values.benefitDescription,
          type: values.benefitType,
          benefitTag: values.benefitTag,
          // `?? 0` y no truthiness: 0 es un valor válido (beneficio sin canje de puntos).
          pointsCost: values.pointsCost ?? 0,
          ...(values.startDate && { startDate: `${values.startDate}T00:00:00` }),
          ...(values.endDate && { endDate: `${values.endDate}T23:59:59` }),
          imageUrl,
        },
      });

      /*
       * `replace` y no `href`: reemplaza la entrada del formulario en el historial en
       * vez de agregar una nueva.
       *
       * Con `href`, el botón "atrás" volvía al formulario —vacío, porque se remonta— y
       * desde ahí se podía reenviar. El dedup del back lo frena con un 400, pero en ese
       * contexto el mensaje se lee como un error del sistema. Con `replace` el
       * formulario deja de estar en el historial y "atrás" lleva a la pantalla anterior
       * al alta.
       */
      window.location.replace(service.callbackUrl(true, code));
    } catch (error: any) {
      setUploadStatus(null);
      setIsSubmitting(false);

      const status = error?.response?.status;
      const message = error?.response?.data?.message;

      // 400 y 429 son accionables por el comercio, así que se muestran acá en vez de
      // mandarlo a la pantalla de error, donde perdería todo lo que cargó.
      if (status === 400 || status === 429) {
        toast({
          title:
            status === 429
              ? 'Demasiados intentos'
              : 'Revisa los datos del formulario',
          description:
            typeof message === 'string'
              ? message
              : 'Es posible que ya exista una solicitud con ese email.',
          status: 'error',
          position: 'top',
          duration: 9000,
          isClosable: true,
        });
        return;
      }

      /*
       * Acá sí `href`: la solicitud no se envió, así que conviene que "atrás" devuelva
       * al formulario. Se pierde lo cargado igual porque se remonta, pero al menos hay
       * un camino de vuelta.
       */
      window.location.href = service.callbackUrl(false);
    }
  };

  const formik = useFormik<ApplicationFormValues>({
    initialValues: {
      name: '',
      types: [],
      address: '',
      state: '',
      city: '',
      postalCode: '',
      point: { lat: null, lng: null },
      locationMetadata: null,
      contactFirstName: '',
      contactLastName: '',
      email: '',
      password: '',
      country: null,
      phone: '',
      whatsapp: '',
      web: '',
      instagram: '',
      benefitType: 'discount',
      benefitTag: '',
      benefitTitle: '',
      benefitDescription: '',
      pointsCost: null,
      startDate: '',
      endDate: '',
      benefitFile: null,
    },
    validationSchema: ApplicationSchema,
    /*
     * `validateOnChange: false` para no marcar en rojo mientras se escribe; la validación
     * corre al salir del campo.
     *
     * Contrapartida: los controles que no hacen blur —chips, select, el recortador de
     * imagen— nunca dispararían la validación y se quedarían con el error viejo aunque
     * ya estén completos. Por eso cada uno de esos pasa `true` como tercer argumento de
     * `setFieldValue`, que fuerza la validación de ese cambio.
     */
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: handleSubmit,
  });

  /**
   * Errores del paso visible. Se calcula sobre `formik.errors` y no sobre `touched`
   * porque el resumen aparece recién cuando se intenta avanzar, y ahí se marcan todos.
   */
  const stepErrors = useMemo<ApplicationFieldError[]>(() => {
    if (!showErrors) return [];

    const seen = new Set<string>();

    return stepFields(step, formik.values).reduce<ApplicationFieldError[]>((acc, field) => {
      const message = field
        .split('.')
        .reduce<any>((value, key) => value?.[key], formik.errors);

      if (!message || typeof message !== 'string') return acc;

      const label = FIELD_LABELS[field] ?? field;
      // Dirección aparece en dos campos (`address` y `point.lat`) con el mismo mensaje.
      if (seen.has(label)) return acc;
      seen.add(label);

      return [...acc, { field, label, message }];
    }, []);
  }, [showErrors, step, formik.errors, formik.values]);

  const validateStep = async (): Promise<boolean> => {
    await formik.setTouched(STEP_TOUCHED[step], true);
    // Fuera del mapa de `touched` por el tipado de File: sin esto el error de "falta la
    // foto" sólo aparecería en el resumen, no en el campo.
    if (step === 1) await formik.setFieldTouched('benefitFile', true, false);

    const errors = await formik.validateForm();

    const hasErrors = stepFields(step, formik.values).some((field) =>
      field.split('.').reduce<any>((value, key) => value?.[key], errors)
    );

    setShowErrors(hasErrors);

    // La especificación pide hacer foco en el primer campo inválido en vez de mostrar un
    // resumen: el panel es angosto y no hay lugar para la tarjeta.
    if (hasErrors) {
      const first = stepFields(step, formik.values).find((field) =>
        field.split('.').reduce<any>((value, key) => value?.[key], errors)
      );

      if (first) focusField(first);
    }

    return !hasErrors;
  };

  const goNext = async () => {
    if (!(await validateStep())) return;

    setStep(1);
    setShowErrors(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStep(0);
    setShowErrors(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    if (!(await validateStep())) return;

    formik.submitForm();
  };

  const focusField = (field: string) => {
    const element = document.getElementById(field);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (element as HTMLElement | null)?.focus?.();
  };

  const searchAddress = useCallback(
    async (query: string) => {
      const trimmed = query.trim();

      if (trimmed.length < ADDRESS_MIN_CHARS) {
        setAddressResults([]);
        setAddressNotice(
          `Escribe al menos ${ADDRESS_MIN_CHARS} caracteres para buscar.`
        );
        return;
      }

      setIsSearchingAddress(true);
      setAddressNotice(null);

      try {
        const results = await service.searchAddress(trimmed);
        setAddressResults(results);

        // Sin este aviso, una búsqueda sin resultados no muestra nada y parece que el
        // buscador no funcionó.
        if (!results.length) {
          setAddressNotice(
            'No encontramos esa dirección. Prueba con la calle y el número.'
          );
        }
      } catch (error) {
        setAddressResults([]);
        setAddressNotice('No pudimos buscar la dirección. Intenta de nuevo.');
      } finally {
        setIsSearchingAddress(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Editar el texto invalida la dirección elegida: si no, alguien podía seleccionar una
   * dirección, cambiar el texto y enviar el formulario con las coordenadas de la anterior.
   */
  const changeAddressQuery = (value: string) => {
    setAddressQuery(value);
    setAddressResults([]);
    setAddressNotice(null);

    if (formik.values.point.lat !== null) {
      formik.setFieldValue('address', '', false);
      formik.setFieldValue('point', { lat: null, lng: null }, false);
      formik.setFieldValue('state', '', false);
      formik.setFieldValue('city', '', false);
      formik.setFieldValue('postalCode', '', false);
      formik.setFieldValue('locationMetadata', null, false);
      // El país se mantiene: es lo único que el comercio pudo haber elegido a mano antes
      // de buscar, y volver a vaciarlo lo obligaría a elegirlo dos veces.
      setIsCountryDetected(false);
    }
  };

  const selectAddress = (result: ApplicationAddressDTO) => {
    setAddressQuery(result.displayName);
    formik.setFieldValue('address', result.displayName, false);
    formik.setFieldValue(
      'point',
      { lat: parseFloat(result.lat), lng: parseFloat(result.lng) },
      false
    );
    formik.setFieldValue('state', result.address?.state ?? '', false);
    formik.setFieldValue('city', result.address?.city ?? '', false);
    formik.setFieldValue('postalCode', result.address?.postalCode ?? '', false);

    /*
     * El país se resuelve acá y no se vuelve a pedir: el buscador devuelve el ISO2
     * (`countryCode`) y de ahí salen la bandera y el prefijo telefónico. Sólo se pisa el
     * valor si el ISO existe en el catálogo; si no, queda el que haya elegido el comercio.
     */
    const detected = COUNTRIES.find(
      // `iso2` y no `iso`: el `iso` de `states-cities-db` es el de tres letras (`ARG`) y
      // el geocoder devuelve el de dos (`AR`).
      (item) => item.iso2 === result.address?.countryCode
    );

    if (detected) {
      formik.setFieldValue(
        'country',
        {
          label: detected.name,
          value: detected.iso2,
          prefix: detected.prefix,
        },
        false
      );
      setIsCountryDetected(true);
    }

    formik.setFieldValue(
      'locationMetadata',
      {
        displayName: result.displayName,
        lat: result.lat,
        lng: result.lng,
        ...Object.fromEntries(
          Object.entries(result.address ?? {}).filter(([, value]) => !!value)
        ),
      },
      false
    );
    formik.setFieldError('address', undefined);
    formik.setFieldError('point.lat', undefined);
    formik.setFieldError('point.lng', undefined);
    formik.setFieldError('state', undefined);
    formik.setFieldError('city', undefined);
    setAddressResults([]);
    setAddressNotice(null);
  };

  const toggleType = (typeId: string) => {
    const current = formik.values.types ?? [];

    formik.setFieldValue(
      'types',
      current.includes(typeId)
        ? current.filter((id) => id !== typeId)
        : [...current, typeId],
      true
    );
  };

  /**
   * Cambiar de tipo limpia el valor: un `15%` heredado de "descuento" no significa nada
   * en "promoción", y en "canje" el campo no se muestra pero se enviaría igual.
   */
  const setBenefitType = (value: string) => {
    formik.setFieldValue('benefitType', value, true);
    formik.setFieldValue('benefitTag', '', true);
  };

  // La vista previa del rail necesita mostrar la foto elegida, no la subida (todavía no
  // se subió). Se revoca la anterior para no acumular object URLs.
  useEffect(() => {
    const file = formik.values.benefitFile;
    if (!file) {
      setBenefitPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setBenefitPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [formik.values.benefitFile]);

  const isStepComplete = stepFields(step, formik.values).every((field) => {
    const value = field
      .split('.')
      .reduce<any>((current, key) => current?.[key], formik.values);

    return Array.isArray(value) ? value.length > 0 : !!value;
  });

  return {
    formik,
    step,
    goNext,
    goBack,
    submit,
    isSubmitting,
    uploadStatus,
    stepErrors,
    focusField,
    isStepComplete,
    types,
    isLoadingTypes,
    typesError,
    retryTypes: loadTypes,
    addressResults,
    isSearchingAddress,
    addressNotice,
    addressQuery,
    changeAddressQuery,
    isCountryDetected,
    searchAddress,
    selectAddress,
    toggleType,
    setBenefitType,
    benefitPreviewUrl,
  };
};
